/**
 * Pure data-shaping helpers for the Weblabs dashboard.
 *
 * The dashboard fetches one grouped row per (day, marketplace/country, weblab)
 * from `sandbox.finn_preprocessed_data_weblabs` (see useWeblabsData). These
 * helpers turn that flat row set into the per-day series Recharts needs, the
 * distinct filter options, a per-weblab attribute index (for the drill-in),
 * and the KPI numbers — all as pure functions (no React, no I/O) so the math
 * is testable in isolation (mirrors src/audit/usageChart.ts).
 */

/** Which revenue-impact metric the charts display. */
export type WeblabMetric = "impact_sb_rev_day" | "impact_sp_rev_day";

/** One grouped row as returned by the weblabs query. */
export interface WeblabImpactRow {
  day: string;
  marketplace_id?: string | number | null;
  country?: string | null;
  weblab: string;
  weblab_type?: string | null;
  primary_owner?: string | null;
  activation_state?: string | null;
  weblab_start_date?: string | null;
  weblab_launch_date?: string | null;
  impact_sb_rev_day?: number | string | null;
  impact_sp_rev_day?: number | string | null;
  sb_rev_annualized?: number | string | null;
  sp_rev_annualized?: number | string | null;
}

/** The annualized-impact column that pairs with a daily-impact metric. */
export type AnnualizedField = "sb_rev_annualized" | "sp_rev_annualized";

/** The annualized column matching the chosen daily metric (SP↔SP, SB↔SB). */
export function annualizedFieldFor(metric: WeblabMetric): AnnualizedField {
  return metric === "impact_sp_rev_day"
    ? "sp_rev_annualized"
    : "sb_rev_annualized";
}

/** Client-side categorical filter selections ("" / undefined = All). */
export interface WeblabsFilterSelections {
  country?: string;
  activation_state?: string;
  weblab_type?: string;
  weblab?: string;
  weblab_launch_date?: string;
}

/** Distinct dropdown options derived from the fetched rows. */
export interface WeblabsOptions {
  countries: string[];
  activationStates: string[];
  weblabTypes: string[];
  weblabs: string[];
  launchDates: string[];
}

/** Per-weblab attributes for the legend + drill-in panel. */
export interface WeblabInfo {
  weblab: string;
  weblab_type: string | null;
  primary_owner: string | null;
  activation_state: string | null;
  weblab_launch_date: string | null;
  weblab_start_date: string | null;
  countries: string[];
}

/** One point on the net daily-impact chart: one value per weblab + the net. */
export interface DailyPoint {
  day: string;
  net: number;
  [weblab: string]: number | string;
}

/** Label for the combined bucket when there are more weblabs than colors. */
export const OTHER_WEBLAB = "Other";

/** Max individual weblab series before the rest collapse into "Other". */
export const MAX_SERIES = 12;

/**
 * Categorical color palette for the stacked weblab bands. Brand-neutral,
 * readable in light and dark. Callers cycle by index; keep length >= a few.
 */
export const WEBLAB_COLORS = [
  "#2c78d4",
  "#e08a1e",
  "#2e9e7b",
  "#c0504d",
  "#7d5ba6",
  "#4aa3df",
  "#d9a441",
  "#3f9d6d",
  "#b5546f",
  "#6c8ebf",
  "#8c6d31",
  "#5aa469",
] as const;

export function colorForIndex(i: number): string {
  return WEBLAB_COLORS[i % WEBLAB_COLORS.length];
}

/** Coerce a possibly-string numeric cell to a number, or null. */
export function toNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** The metric value for a row, as a number (0 when null/blank). */
export function metricValue(
  row: WeblabImpactRow,
  metric: WeblabMetric
): number {
  return toNum(row[metric]) ?? 0;
}

/**
 * The weblab's run/launch date for a row: the run date (weblab_start_date) when
 * activation_state='run', otherwise weblab_launch_date. Used by the client-side
 * Run/Launch Date filter. Returns "" when unavailable.
 */
export function runLaunchDate(row: WeblabImpactRow): string {
  const state = String(row.activation_state ?? "");
  const d = state === "run" ? row.weblab_start_date : row.weblab_launch_date;
  return String(d ?? "");
}

/**
 * Apply the client-side categorical filters. Any selection that is empty /
 * undefined means "All" and is skipped. Date range is applied server-side, so
 * it is intentionally not handled here.
 */
export function filterRows(
  rows: WeblabImpactRow[],
  sel: WeblabsFilterSelections
): WeblabImpactRow[] {
  return rows.filter((r) => {
    if (sel.country && (r.country ?? "") !== sel.country) return false;
    if (
      sel.activation_state &&
      (r.activation_state ?? "") !== sel.activation_state
    )
      return false;
    if (sel.weblab_type && (r.weblab_type ?? "") !== sel.weblab_type)
      return false;
    if (sel.weblab && r.weblab !== sel.weblab) return false;
    if (
      sel.weblab_launch_date &&
      (r.weblab_launch_date ?? "") !== sel.weblab_launch_date
    )
      return false;
    return true;
  });
}

/** Sorted-unique non-empty string values of a field across rows. */
function distinct(
  rows: WeblabImpactRow[],
  pick: (r: WeblabImpactRow) => unknown
): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = pick(r);
    if (v != null && String(v).trim() !== "") set.add(String(v));
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Distinct dropdown options for every categorical filter. */
export function collectOptions(rows: WeblabImpactRow[]): WeblabsOptions {
  return {
    countries: distinct(rows, (r) => r.country),
    activationStates: distinct(rows, (r) => r.activation_state),
    weblabTypes: distinct(rows, (r) => r.weblab_type),
    weblabs: distinct(rows, (r) => r.weblab),
    launchDates: distinct(rows, (r) => r.weblab_launch_date),
  };
}

/** Sum of the chosen metric per weblab across all rows. */
export function weblabTotals(
  rows: WeblabImpactRow[],
  metric: WeblabMetric
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const r of rows) {
    totals.set(r.weblab, (totals.get(r.weblab) ?? 0) + metricValue(r, metric));
  }
  return totals;
}

/**
 * Per-weblab annualized impact for the given column. Unlike the daily impact,
 * the annualized value is a per-weblab constant (not a daily flow), so we do
 * NOT sum it across days — we take the largest-magnitude value seen for the
 * weblab (the fullest-marketplace figure). Weblabs with no value are omitted.
 */
export function weblabAnnualized(
  rows: WeblabImpactRow[],
  field: AnnualizedField
): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const v = toNum(r[field]);
    if (v == null) continue;
    const cur = m.get(r.weblab);
    if (cur == null || Math.abs(v) > Math.abs(cur)) m.set(r.weblab, v);
  }
  return m;
}

/**
 * Choose which weblabs get their own stacked band. The top `maxSeries` by
 * absolute total impact are kept; any remainder collapse into one "Other"
 * series so the chart stays readable and colors don't run out.
 */
export function selectSeriesWeblabs(
  rows: WeblabImpactRow[],
  metric: WeblabMetric,
  maxSeries: number = MAX_SERIES
): { series: string[]; hasOther: boolean } {
  const totals = weblabTotals(rows, metric);
  const ranked = Array.from(totals.keys()).sort(
    (a, b) => Math.abs(totals.get(b)!) - Math.abs(totals.get(a)!)
  );
  if (ranked.length <= maxSeries) {
    return { series: ranked, hasOther: false };
  }
  return { series: ranked.slice(0, maxSeries), hasOther: true };
}

/**
 * Build the per-day series for the net daily-impact chart.
 *
 * For each day, sums the chosen metric per weblab (across all marketplaces /
 * countries in `rows`) into one value per kept weblab (plus an "Other" bucket
 * when there are more weblabs than `maxSeries`), and a `net` = sum of every
 * weblab that day. Days are returned in ascending order.
 */
export function buildDailySeries(
  rows: WeblabImpactRow[],
  metric: WeblabMetric,
  maxSeries: number = MAX_SERIES
): { data: DailyPoint[]; series: string[]; hasOther: boolean } {
  const { series, hasOther } = selectSeriesWeblabs(rows, metric, maxSeries);
  const seriesSet = new Set(series);
  const byDay = new Map<string, DailyPoint>();

  for (const r of rows) {
    const day = r.day;
    if (!day) continue;
    let point = byDay.get(day);
    if (!point) {
      point = { day, net: 0 };
      for (const w of series) point[w] = 0;
      if (hasOther) point[OTHER_WEBLAB] = 0;
      byDay.set(day, point);
    }
    const val = metricValue(r, metric);
    const bucket = seriesSet.has(r.weblab) ? r.weblab : OTHER_WEBLAB;
    if (bucket === OTHER_WEBLAB && !hasOther) {
      // Should not happen (bucket only "Other" when hasOther), but guard.
      point[r.weblab] = ((point[r.weblab] as number) ?? 0) + val;
    } else {
      point[bucket] = ((point[bucket] as number) ?? 0) + val;
    }
    point.net += val;
  }

  const data = Array.from(byDay.values()).sort((a, b) =>
    a.day.localeCompare(b.day)
  );
  const displaySeries = hasOther ? [...series, OTHER_WEBLAB] : series;
  return { data, series: displaySeries, hasOther };
}

/** One point on the signed (positive/negative) daily chart. */
export interface SignedDailyPoint {
  day: string;
  pos: number;
  neg: number;
  net: number;
  [k: string]: number | string;
}

/**
 * Build the per-day POSITIVE / NEGATIVE split for the net daily-impact chart.
 *
 * A weblab's sign is decided on its per-day total (summed across marketplaces),
 * then each day's `pos` = sum of all positive weblab totals and `neg` = sum of
 * all negative ones, so `net = pos + neg`. This drives a two-hue diverging
 * chart (green above zero, red below) that reads positive-vs-negative at a
 * glance, instead of one color per weblab. Days ascending.
 */
export function buildSignedDailySeries(
  rows: WeblabImpactRow[],
  metric: WeblabMetric
): SignedDailyPoint[] {
  const byDayWeblab = new Map<string, Map<string, number>>();
  for (const r of rows) {
    if (!r.day) continue;
    let m = byDayWeblab.get(r.day);
    if (!m) {
      m = new Map<string, number>();
      byDayWeblab.set(r.day, m);
    }
    m.set(r.weblab, (m.get(r.weblab) ?? 0) + metricValue(r, metric));
  }
  const out: SignedDailyPoint[] = [];
  for (const [day, m] of byDayWeblab) {
    let pos = 0;
    let neg = 0;
    for (const v of m.values()) {
      if (v > 0) pos += v;
      else if (v < 0) neg += v;
    }
    out.push({ day, pos, neg, net: pos + neg });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}

/** X-axis grouping for the chart. */
export type Grouping = "D" | "W" | "M" | "Q";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Bucket a `YYYY-MM-DD` day into the grouping's SORT KEY (kept chronologically
 * sortable): D → the day; W → the Saturday ending its Sun–Sat week
 * (week-ending date, YYYY-MM-DD); M → `YYYY-MM`; Q → `YYYY-Qn`.
 * Use `bucketLabel` for the human-readable display.
 */
export function bucketKey(day: string, grouping: Grouping): string {
  if (grouping === "D") return day;
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  if (grouping === "M") return `${y}-${String(m).padStart(2, "0")}`;
  if (grouping === "Q") return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
  // Week → the Saturday that ends its Sun–Sat week (week-ending date).
  const dt = new Date(Date.UTC(y, m - 1, d));
  const daysToSaturday = 6 - dt.getUTCDay(); // getUTCDay: 0=Sun … 6=Sat
  dt.setUTCDate(dt.getUTCDate() + daysToSaturday);
  const wy = dt.getUTCFullYear();
  const wm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const wd = String(dt.getUTCDate()).padStart(2, "0");
  return `${wy}-${wm}-${wd}`;
}

/** Week number of a `YYYY-MM-DD` date = ceil(day-of-year / 7). */
function weekOfYear(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  const dayOfYear =
    Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86400000) + 1;
  return Math.ceil(dayOfYear / 7);
}

/**
 * Human-readable display label for a bucket key:
 *   W → `WK{n} ({M/D})` on the week-ending Saturday (e.g. "WK32 (8/8)");
 *   M → `Mon YYYY` (e.g. "Aug 2026"); D / Q → the key itself.
 */
export function bucketLabel(key: string, grouping: Grouping): string {
  if (grouping === "W") {
    const [y, m, d] = key.split("-").map(Number);
    if (y && m && d) return `WK${weekOfYear(key)} (${m}/${d})`;
  }
  if (grouping === "M") {
    const [y, m] = key.split("-").map(Number);
    if (y && m && m >= 1 && m <= 12) return `${MONTH_NAMES[m - 1]} ${y}`;
  }
  return key;
}

/** YYYY-MM-DD for a UTC date. */
function isoUTC(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Inverse of `bucketKey`: the inclusive [start, end] day range a bucket key
 * covers. Used to fetch a clicked bucket's per-weblab breakdown on demand.
 *   D → the day itself; W → Sunday…Saturday (key is the week-ending Saturday);
 *   M → first…last day of the month; Q → first…last day of the quarter.
 */
export function bucketRange(
  key: string,
  grouping: Grouping
): { start: string; end: string } {
  if (grouping === "D") return { start: key, end: key };
  if (grouping === "M") {
    const [y, m] = key.split("-").map(Number);
    const end = new Date(Date.UTC(y, m, 0)); // day 0 of next month = last day
    return { start: `${y}-${String(m).padStart(2, "0")}-01`, end: isoUTC(end) };
  }
  if (grouping === "Q") {
    const [yStr, qStr] = key.split("-Q");
    const y = Number(yStr);
    const startMonth = (Number(qStr) - 1) * 3; // 0-based
    const start = new Date(Date.UTC(y, startMonth, 1));
    const end = new Date(Date.UTC(y, startMonth + 3, 0));
    return { start: isoUTC(start), end: isoUTC(end) };
  }
  // W: key is the week-ending Saturday; start = the preceding Sunday.
  const [y, m, d] = key.split("-").map(Number);
  const sun = new Date(Date.UTC(y, m - 1, d));
  sun.setUTCDate(sun.getUTCDate() - 6);
  return { start: isoUTC(sun), end: key };
}

/**
 * Aggregate a per-day series into the chosen grouping (D returns it unchanged).
 * Sums each weblab band and the net within each bucket; buckets are ordered
 * chronologically by their key.
 */
export function bucketSeries(
  daily: DailyPoint[],
  seriesKeys: string[],
  grouping: Grouping
): DailyPoint[] {
  if (grouping === "D") return daily;
  const byBucket = new Map<string, DailyPoint>();
  for (const p of daily) {
    const key = bucketKey(p.day, grouping);
    let b = byBucket.get(key);
    if (!b) {
      b = { day: key, net: 0 };
      for (const w of seriesKeys) b[w] = 0;
      byBucket.set(key, b);
    }
    for (const w of seriesKeys) {
      b[w] = (b[w] as number) + Number(p[w] ?? 0);
    }
    b.net += Number(p.net ?? 0);
  }
  return Array.from(byBucket.values()).sort((a, b) =>
    a.day.localeCompare(b.day)
  );
}

/**
 * Per-bucket annualized totals for the chart tooltip. Annualized is a
 * per-weblab CONSTANT (not a daily flow), so within each bucket we count each
 * weblab's value once (largest magnitude seen), then split by sign. Returns
 * bucketKey → { pos, neg, net }. This is context for the hover popup only — it
 * is never plotted (the axis stays impact-per-day).
 */
export function buildAnnualizedByBucket(
  rows: WeblabImpactRow[],
  field: AnnualizedField,
  grouping: Grouping
): Map<string, { pos: number; neg: number; net: number }> {
  const byBucket = new Map<string, Map<string, number>>();
  for (const r of rows) {
    if (!r.day) continue;
    const v = toNum(r[field]);
    if (v == null) continue;
    const key = bucketKey(r.day, grouping);
    let m = byBucket.get(key);
    if (!m) {
      m = new Map<string, number>();
      byBucket.set(key, m);
    }
    const cur = m.get(r.weblab);
    if (cur == null || Math.abs(v) > Math.abs(cur)) m.set(r.weblab, v);
  }
  const out = new Map<string, { pos: number; neg: number; net: number }>();
  for (const [key, m] of byBucket) {
    let pos = 0;
    let neg = 0;
    for (const v of m.values()) {
      if (v > 0) pos += v;
      else if (v < 0) neg += v;
    }
    out.set(key, { pos, neg, net: pos + neg });
  }
  return out;
}

/** Daily series for a single weblab (drill-in mini chart), ascending by day. */
export function buildWeblabDailySeries(
  rows: WeblabImpactRow[],
  weblab: string,
  metric: WeblabMetric
): { day: string; value: number }[] {
  const byDay = new Map<string, number>();
  for (const r of rows) {
    if (r.weblab !== weblab || !r.day) continue;
    byDay.set(r.day, (byDay.get(r.day) ?? 0) + metricValue(r, metric));
  }
  return Array.from(byDay.entries())
    .map(([day, value]) => ({ day, value }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

/** Per-weblab attribute index for the legend + drill-in panel. */
export function buildWeblabIndex(rows: WeblabImpactRow[]): WeblabInfo[] {
  const map = new Map<string, WeblabInfo & { _countries: Set<string> }>();
  for (const r of rows) {
    let info = map.get(r.weblab);
    if (!info) {
      info = {
        weblab: r.weblab,
        weblab_type: r.weblab_type ?? null,
        primary_owner: r.primary_owner ?? null,
        activation_state: r.activation_state ?? null,
        weblab_launch_date: r.weblab_launch_date ?? null,
        weblab_start_date: r.weblab_start_date ?? null,
        countries: [],
        _countries: new Set<string>(),
      };
      map.set(r.weblab, info);
    }
    if (r.country) info._countries.add(String(r.country));
  }
  return Array.from(map.values())
    .map(({ _countries, ...info }) => ({
      ...info,
      countries: Array.from(_countries).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.weblab.localeCompare(b.weblab));
}

/** KPI summary numbers over the (already filtered) rows. */
export interface WeblabsKpis {
  totalNet: number;
  weblabCount: number;
  topPositive: { weblab: string; total: number } | null;
  topNegative: { weblab: string; total: number } | null;
}

export function computeKpis(
  rows: WeblabImpactRow[],
  metric: WeblabMetric
): WeblabsKpis {
  const totals = weblabTotals(rows, metric);
  let totalNet = 0;
  let topPositive: { weblab: string; total: number } | null = null;
  let topNegative: { weblab: string; total: number } | null = null;
  for (const [weblab, total] of totals) {
    totalNet += total;
    if (total > 0 && (!topPositive || total > topPositive.total)) {
      topPositive = { weblab, total };
    }
    if (total < 0 && (!topNegative || total < topNegative.total)) {
      topNegative = { weblab, total };
    }
  }
  return { totalNet, weblabCount: totals.size, topPositive, topNegative };
}

/**
 * Signed, scaled currency label for axis ticks / tooltips / KPIs. Reuses the
 * dashboard's scale helpers so negatives render as "-$1.2MM" etc.
 */
export function formatSignedUsd(value: number | null, scale: number): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value) / scale;
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  // Thousands separators for readability (e.g. "$235,885,998").
  const body = abs.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${sign}$${body}`;
}
